import React from 'react';
import { DollarSign, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn, formatCurrency } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { EXPENSE_TYPES } from './sponsorshipConstants';

export const ChildExpenseFormDialog = ({ open, onOpenChange, form, setForm, onSubmit, editing, children }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogTrigger asChild>
      <Button size="sm" className="bg-pink-600 hover:bg-pink-700" data-testid="add-expense-btn">
        <DollarSign className="mr-1 h-4 w-4" />
        Record Expense
      </Button>
    </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-heading">{editing ? 'Edit Child Expense' : 'Record Child Expense'}</DialogTitle>
        <DialogDescription>{editing ? `Update this expense for ${editing.child_name} — balance re-adjusts automatically` : 'Record an expense for a sponsored child'}</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Select Child *</Label>
          <Select
            value={form.child_id}
            onValueChange={(value) => setForm({ ...form, child_id: value })}
            required
            disabled={!!editing}
          >
            <SelectTrigger data-testid="expense-child-select">
              <SelectValue placeholder="Choose child" />
            </SelectTrigger>
            <SelectContent>
              {children.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} - Balance: {formatCurrency(c.account_balance)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Expense Type *</Label>
            <Select
              value={form.expense_type}
              onValueChange={(value) => setForm({ ...form, expense_type: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {EXPENSE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Amount (UGX) *</Label>
            <Input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="Amount"
              required
            />
          </div>
        </div>
        {form.expense_type === 'other' && (
          <div className="space-y-2">
            <Label>Custom Expense Name *</Label>
            <Input
              value={form.custom_type_name}
              onChange={(e) => setForm({ ...form, custom_type_name: e.target.value })}
              placeholder="e.g., Transport, Books"
              required
            />
          </div>
        )}
        <div className="space-y-2">
          <Label>Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(form.date, 'PPP')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={form.date}
                onSelect={(date) => setForm({ ...form, date })}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Optional description"
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" className="bg-pink-600 hover:bg-pink-700" data-testid="expense-submit-btn">{editing ? 'Save Changes' : 'Record Expense'}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);
